<script setup lang="ts">
import type { MultiSelectListProps } from './multi-select-list';
import ColorTag from '../ColorTag/ColorTag.vue';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import TagV2 from '../TagV2/TagV2.vue';

const props = withDefaults(defineProps<MultiSelectListProps>(), {
  emptyText: '',
  borderless: false,
  selectedIcon: undefined,
  selectedIconClass: 'text-slate-400',
  leadingSelectedIcon: undefined,
  leadingUnselectedIcon: undefined,
  leadingIconClass: 'text-slate-400',
});

const emits =
  defineEmits<(event: 'update:selectedIds', selectedIds: string[]) => void>();

const isSelected = (id: string): boolean => {
  return props.selectedIds.includes(id);
};

const toggleSelectedIds = (id: string): string[] => {
  const selectedIds = new Set(props.selectedIds);
  if (selectedIds.has(id)) {
    selectedIds.delete(id);
  } else {
    selectedIds.add(id);
  }

  return [...selectedIds];
};

const onToggleItem = (id: string): void => {
  emits('update:selectedIds', toggleSelectedIds(id));
};
</script>

<template>
  <div
    class="flex w-full flex-col overflow-y-auto bg-white"
    :class="props.borderless ? '' : 'rounded-xl border border-slate-200'"
  >
    <button
      v-for="item in props.items"
      :key="item.id"
      type="button"
      class="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0"
      :class="
        isSelected(item.id) ? 'bg-primary-50/70' : 'bg-white hover:bg-slate-50'
      "
      @click="onToggleItem(item.id)"
    >
      <template v-if="props.leadingSelectedIcon && props.leadingUnselectedIcon">
        <HugeIcon
          :icon="
            isSelected(item.id)
              ? props.leadingSelectedIcon
              : props.leadingUnselectedIcon
          "
          :size="18"
          class="shrink-0"
          :class="props.leadingIconClass"
        />
      </template>
      <span
        v-else
        class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors"
        :class="
          isSelected(item.id)
            ? 'border-primary-500 bg-white text-white'
            : 'border-slate-300 bg-white'
        "
      >
        <HugeIcon
          v-if="isSelected(item.id)"
          icon="checkmark"
          :size="16"
          class="text-primary-500"
        />
      </span>

      <ColorTag v-if="item.color" :background-color="item.color" />
      <HugeIcon
        v-else-if="item.icon"
        :icon="item.icon"
        :size="16"
        class="shrink-0 text-slate-400"
      />

      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span class="truncate text-sm font-semibold text-slate-900">
            {{ item.label }}
          </span>
          <TagV2 v-if="item.tag" :label="item.tag" />
        </div>
        <p
          v-if="item.description"
          class="mt-1 line-clamp-2 text-xs leading-5 text-slate-500"
        >
          {{ item.description }}
        </p>
      </div>

      <HugeIcon
        v-if="props.selectedIcon && isSelected(item.id)"
        :icon="props.selectedIcon"
        :size="16"
        class="shrink-0"
        :class="props.selectedIconClass"
      />
    </button>

    <p
      v-if="props.items.length === 0 && props.emptyText"
      class="py-8 text-center text-sm text-slate-400"
    >
      {{ props.emptyText }}
    </p>
  </div>
</template>
